import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { CheckIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import { usePlans } from '../lib/plansClient';
import Header from './Header';
import { FEATURE_BY_ID } from '../lib/featureCatalog';
import { resolvePlanFeatures as sharedResolve } from '../lib/planFeatures';

// GERÇEK Paket Özellikleri
const PACKAGE_FEATURES = {
  starter: [
    'Kişisel Dashboard',
    'Tüm talepleri Görüntüleme',
    'Temel Para Biriminde avans talebi',
    'Standart Avans Talebi',
    'Standart Avans Kapama Süresi',
    'Avans Onay Fonksiyonu',
    'Avans Kapama Fonksiyonu',
    'Standart Kullanıcı Hiyerarşi',
    'Genel Avans Raporları',
    'Kullanıcı Yönetimi',
    'Kategori Yönetimi',
    'Kullanıcı İzinleri',
    'Rol Bazlı İzinler',
    'Genel Ayarlar',
    'Genişletilmiş Filtreler',
    'Mobil Cihazlardan Tam Erişim'
  ],
  professional: [
    'Profesyonel Dashboard',
    'Tüm talepleri Görüntüleme',
    'Çoklu Para Birimi avans talebi',
    'Profesyonel Avans Talebi Fonksiyonları',
    'Profesyonel Avans Kapama Fonksiyonları',
    'Avans Onay Fonksiyonu',
    'Avans Kapama Fonksiyonu',
    'Profesyonel Poziyon Hiyerarşisi',
    'Genel Avans Raporları',
    'Kullanıcı Yönetimi',
    'Kategori Yönetimi',
    'Kullanıcı İzinleri',
    'Rol Bazlı İzinler',
    'Genel Ayarlar',
    'Genişletilmiş Filtreler',
    'Mobil Cihazlardan Tam Erişim',
    'Finans Onay Fonksiyonu',
    'Avans Kapama Belge Modülü',
    'Cari hsap takip Modülü',
    'Email Bildirimleri',
    'Whatsapp Entegrasyonu',
    'Excel Export',
    'PDF Export',
    'Avans Uyarı Sistemi'
  ],
  enterprise: [
    'Profesyonel Dashboard',
    'Tüm talepleri Görüntüleme',
    'Çoklu Para Birimi avans talebi',
    'Profesyonel Avans Talebi Fonksiyonları',
    'Profesyonel Avans Kapama Fonksiyonları',
    'Avans Onay Fonksiyonu',
    'Avans Kapama Fonksiyonu',
    'Ultra Pozisyon Hiyerarşisi',
    'Genel Avans Raporları',
    'Kullanıcı Yönetimi',
    'Kategori Yönetimi',
    'Kullanıcı İzinleri',
    'Rol Bazlı İzinler',
    'Genel Ayarlar',
    'Genişletilmiş Filtreler',
    'Mobil Cihazlardan Tam Erişim',
    'Finans Onay Fonksiyonu',
    'Avans Kapama Belge Modülü',
    'Cari hsap takip Modülü',
    'Şirket içi Mesajlaşma',
    'İşlem Bazlı Avans Raporu',
    'Kullanıcı Bazlı Avans Raporu',
    'Şirket Bazlı Avans Raporu',
    'Yapay Zeka Önerileri',
    'Email Bildirimleri',
    'Whatsapp Entegrasyonu',
    'Excel Export',
    'PDF Export',
    'Avans Uyarı Sistemi'
  ]
};

const PricingPage = () => {
  const [activePricing, setActivePricing] = useState('yearly');
  const navigate = useNavigate();
  const { plans: backendPlans, loading: plansLoading } = usePlans();

  // Karşılaştırma matriksi (Ultra Admin > Karşılaştırma Matrisi ekranında düzenlenir).
  const [matrix, setMatrix] = useState({ rows: [], plans: [] });
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/plan-comparison`, {
          cache: 'no-store',
        });
        if (res.ok) {
          const data = await res.json();
          setMatrix({ rows: data.rows || [], plans: data.plans || [] });
        }
      } catch { /* fallback aşağıda devreye girer */ }
    };
    load();
    // Ultra Admin kaydettiğinde plansClient'daki BroadcastChannel bize
    // "updated" mesajı atıyor — canlı yenile.
    let bc = null;
    if ('BroadcastChannel' in window) {
      bc = new BroadcastChannel('plans-updated');
      bc.onmessage = (e) => { if (e.data === 'updated') load(); };
    }
    return () => { if (bc) bc.close(); };
  }, []);

  const matrixBySlug = useMemo(() => {
    const m = {};
    for (const p of matrix.plans) m[p.slug] = new Set(p.comparison_features || []);
    return m;
  }, [matrix]);

  // Backend'den gelen verileri normalize et. Öncelik:
  //  1) Ultra Admin > Paket Düzenle > "Ana Sayfa Özellik Maddeleri" (marketing_features)
  //  2) plan.features (teknik feature ID → FEATURE_BY_ID.name)
  //  3) Hard-coded PACKAGE_FEATURES fallback
  const resolveFeatures = (plan) =>
    sharedResolve(plan, {
      featureIdToName: (id) => FEATURE_BY_ID[id]?.name,
      fallbackByPlanId: PACKAGE_FEATURES,
    });

  const normalizePlans = (plans) => {
    return plans.map(plan => ({
      id: plan.id || plan.code,
      name: plan.name || plan.title,
      price_monthly: plan.price_monthly || plan.price,
      price_yearly: plan.price_yearly || plan.price,
      currency: plan.currency?.toLowerCase() || 'usd',
      user_limit: plan.user_limit || plan.userLimit || 0,
      features: resolveFeatures(plan),
      is_featured: plan.is_featured || plan.isFeatured || false
    }));
  };

  // Fallback paketler
  const defaultPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price_monthly: 29,
      price_yearly: 29,
      currency: 'eur',
      user_limit: 5,
      features: PACKAGE_FEATURES.starter,
      is_featured: false
    },
    {
      id: 'professional',
      name: 'Professional',
      price_monthly: 39,
      price_yearly: 39,
      currency: 'eur',
      user_limit: 15,
      features: PACKAGE_FEATURES.professional,
      is_featured: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price_monthly: 49,
      price_yearly: 49,
      currency: 'eur',
      user_limit: 50,
      features: PACKAGE_FEATURES.enterprise,
      is_featured: false
    }
  ];

  const plans = backendPlans.length > 0 ? normalizePlans(backendPlans) : defaultPlans;
  const filteredPlans = plans.filter(p => p.id !== 'free_trial');

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'try': '₺',
      'usd': '$',
      'eur': '€',
      'gbp': '£'
    };
    return symbols[currency?.toLowerCase()] || '$';
  };

  // Karşılaştırma tablosu satırları:
  //   1) Ultra Admin > Karşılaştırma Matrisi'nde tanımlanmış satırlar (öncelik)
  //   2) Yoksa mevcut planların marketing_features'larının birleşimi (fallback)
  //
  // Satır şekli: {label, category}. Legacy string desteği için normalize edilir.
  const rowObjects = useMemo(() => {
    if (matrix.rows.length > 0) {
      return matrix.rows.map(r => (typeof r === 'string'
        ? { label: r, category: '' }
        : { label: r.label || '', category: r.category || '' }));
    }
    const union = [...new Set(filteredPlans.flatMap((p) => p.features || []))];
    return union.map(l => ({ label: l, category: '' }));
  }, [matrix.rows, filteredPlans]);

  // Kategoriye göre grupla — sıra korunur (Ultra Admin'de tanımlanan sıra).
  const groupedRows = useMemo(() => {
    const groups = [];
    const idxByCat = {};
    for (const row of rowObjects) {
      const cat = row.category || '';
      if (!(cat in idxByCat)) {
        idxByCat[cat] = groups.length;
        groups.push({ category: cat, rows: [] });
      }
      groups[idxByCat[cat]].rows.push(row);
    }
    return groups;
  }, [rowObjects]);

  // İşaretli mi?
  //   1) Ultra Admin matriksinde `plan.comparison_features` seti içindeyse ✓
  //   2) Yoksa fallback: plan.features içinde ise ✓
  const hasFeature = (planSlug, label) => {
    if (matrix.rows.length > 0 && matrixBySlug[planSlug]) {
      return matrixBySlug[planSlug].has(label);
    }
    const plan = filteredPlans.find((p) => p.id === planSlug);
    return (plan?.features || []).includes(label);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Ana Sayfaya Dön
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Detaylı Paket Karşılaştırması
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Size en uygun paketi seçin ve tüm özellikleri keşfedin
          </p>

          {/* Pricing Toggle */}
          <div className="flex items-center justify-center mb-8">
            <span className={`mr-3 ${activePricing === 'monthly' ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
              Aylık
            </span>
            <button 
              onClick={() => setActivePricing(activePricing === 'monthly' ? 'yearly' : 'monthly')}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-teal-600 transition-colors"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${activePricing === 'yearly' ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className={`ml-3 ${activePricing === 'yearly' ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
              Yıllık
            </span>
            <span className="ml-2 bg-teal-100 text-teal-800 px-2 py-1 rounded-full text-sm font-medium">
              %20 İndirim
            </span>
          </div>
        </div>

        {/* Comparison — Desktop table (hidden on <md) */}
        <div className="hidden md:block bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 bg-gray-50 sticky left-0 z-10">
                    Özellikler
                  </th>
                  {filteredPlans.map((plan) => {
                    const currencySymbol = getCurrencySymbol(plan.currency);
                    const monthlyPrice = plan.price_monthly || 0;
                    const yearlyPrice = activePricing === 'yearly' 
                      ? Math.round(monthlyPrice * 12 * 0.8)
                      : monthlyPrice;
                    const displayPrice = activePricing === 'yearly' ? yearlyPrice : monthlyPrice;

                    return (
                      <th key={plan.id} className={`px-6 py-4 text-center ${plan.is_featured ? 'bg-purple-50' : 'bg-gray-50'}`}>
                        <div className="space-y-2">
                          {plan.is_featured && (
                            <span className="inline-block bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                              En Popüler
                            </span>
                          )}
                          <div className="text-2xl font-bold text-gray-900">
                            {plan.name}
                          </div>
                          <div className="text-3xl font-bold text-teal-600">
                            {currencySymbol}{displayPrice}
                          </div>
                          <div className="text-sm text-gray-500">/ay</div>
                          <div className="text-sm text-gray-600">
                            {plan.user_limit} kullanıcıya kadar
                          </div>
                          <Link to={`/register?plan=${plan.id}`}>
                            <Button className={`mt-4 ${plan.is_featured ? 'bg-purple-600 hover:bg-purple-700' : 'bg-teal-600 hover:bg-teal-700'} text-white`}>
                              Başlayın
                            </Button>
                          </Link>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {groupedRows.map((group, gIdx) => (
                  <React.Fragment key={`grp-${gIdx}`}>
                    {group.category && (
                      <tr className="bg-gradient-to-r from-teal-50 to-transparent">
                        <td colSpan={filteredPlans.length + 1}
                            className="px-6 py-3 text-xs font-bold uppercase tracking-wider text-teal-700 sticky left-0 z-10 bg-teal-50/80">
                          {group.category}
                        </td>
                      </tr>
                    )}
                    {group.rows.map((row, rIdx) => (
                      <tr key={`${gIdx}-${rIdx}`}
                          className={`border-b border-gray-100 ${rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-6 py-4 text-sm text-gray-900 font-medium sticky left-0 z-10 bg-inherit">
                          {row.label}
                        </td>
                        {filteredPlans.map((plan) => (
                          <td key={plan.id} className="px-6 py-4 text-center">
                            {hasFeature(plan.id, row.label) ? (
                              <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                            ) : (
                              <XIcon className="w-5 h-5 text-gray-300 mx-auto" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Comparison — Mobile card view (md:hidden) */}
        <div className="md:hidden space-y-6" data-testid="pricing-mobile-cards">
          {filteredPlans.map((plan) => {
            const currencySymbol = getCurrencySymbol(plan.currency);
            const monthlyPrice = plan.price_monthly || 0;
            const yearlyPrice = activePricing === 'yearly'
              ? Math.round(monthlyPrice * 12 * 0.8)
              : monthlyPrice;
            const displayPrice = activePricing === 'yearly' ? yearlyPrice : monthlyPrice;
            const planFeatures = plan.features || [];

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl shadow-xl p-6 border ${plan.is_featured ? 'border-2 border-purple-300 ring-2 ring-purple-100' : 'border-gray-200'}`}
                data-testid={`pricing-mobile-card-${plan.id}`}
              >
                <div className="flex items-baseline justify-between mb-4">
                  <div>
                    {plan.is_featured && (
                      <span className="inline-block bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-medium mb-2">
                        En Popüler
                      </span>
                    )}
                    <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                    <p className="text-sm text-gray-500">{plan.user_limit} kullanıcıya kadar</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-teal-600">
                      {currencySymbol}{displayPrice}
                    </div>
                    <div className="text-xs text-gray-500">/ay</div>
                  </div>
                </div>
                <ul className="space-y-2 mb-5">
                  {planFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-700">
                      <CheckIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to={`/register?plan=${plan.id}`}>
                  <Button className={`w-full ${plan.is_featured ? 'bg-purple-600 hover:bg-purple-700' : 'bg-teal-600 hover:bg-teal-700'} text-white`}>
                    Başlayın
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12">
          <p className="text-lg text-gray-600 mb-4">
            Hala karar veremediniz mi?
          </p>
          <Link to="/register?plan=free_trial">
            <Button className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-3">
              14 Gün Ücretsiz Deneyin
            </Button>
          </Link>
          <p className="text-sm text-gray-500 mt-2">
            Kredi kartı gerektirmez • İstediğiniz zaman iptal edin
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
